import { NumericType } from "@stepapp/core/apiTypes";
import { Block, Input, palette, SvgIcon, spacing, useBreakpoints } from "@stepapp/ui";
import { useCallback, useMemo } from "react";
import { StyleSheet, TouchableOpacity } from "react-native";
import PlusMinusCounter from "./PlusMinusCounter";

type QuantityActionsProps = {
  quantity: number;
  onChange: (newValue: number) => void;
  textColor?: string;
  inModal?: boolean;
  singleOption?: boolean;
  numericType?: NumericType | null;
};

const circleSize = spacing.unit * 4.5;
const QuantityActions = ({
  quantity,
  singleOption,
  textColor = palette.common.black,
  inModal = false,
  onChange,
  numericType,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: skip
}: QuantityActionsProps) => {
  const { mobileOnly } = useBreakpoints();
  const isActive = quantity > 0;
  const showLeftActions = inModal || isActive;
  const isActiveNoModal = isActive && !inModal;
  const showEdit = isActiveNoModal && !singleOption;

  const rightActionTextColor = inModal
    ? palette.primary.accent
    : showLeftActions
      ? palette.primary.pinkSalmon
      : palette.common.lightGray;
  const rightActionBackgroundColor = inModal
    ? palette.common.white
    : isActive
      ? palette.primary.accent
      : palette.common.mediumGray;
  const leftActionTextColor = inModal
    ? isActive
      ? palette.primary.accent
      : palette.secondary.grey
    : palette.common.white;
  const leftActionBackgroundColor = isActiveNoModal ? palette.primary.accent : palette.common.white;

  const backgroundSizeStyles = useMemo(() => ({ width: circleSize, height: circleSize }), []);
  const rightIconSize =
    mobileOnly && showLeftActions
      ? 36
      : mobileOnly && !showLeftActions
        ? 34
        : !mobileOnly && showLeftActions
          ? 36
          : 32;

  const handleAddQuantity = useCallback(() => {
    onChange?.(quantity);
  }, [onChange, quantity]);

  const handleChangeQuantity = useCallback(
    (text: string) => {
      onChange?.(Number(text));
    },
    [onChange],
  );

  return (
    <Block alignCenter justifyEnd row>
      {showEdit ? (
        <>
          <TouchableOpacity onPress={handleAddQuantity}>
            <Block
              alignCenter
              justifyCenter
              borderRadius={circleSize / 2}
              style={[backgroundSizeStyles, { backgroundColor: palette.primary.pinkSalmon }]}
            >
              <SvgIcon color={palette.primary.accent} name="edit" size={18} />
            </Block>
          </TouchableOpacity>
        </>
      ) : numericType === NumericType.Input ? (
        <Input
          onChangeText={handleChangeQuantity}
          value={quantity.toString()}
          keyboardType="numeric"
          style={styles.numberInput}
        />
      ) : (
        <PlusMinusCounter
          dimension={mobileOnly ? 4.7 : 4.5}
          leftIconBackground={leftActionBackgroundColor}
          leftIconColor={leftActionTextColor}
          leftIconSize={36}
          onValueChange={onChange}
          rightIconBackground={rightActionBackgroundColor}
          rightIconColor={rightActionTextColor}
          rightIconSize={rightIconSize}
          showLeftIcon={showLeftActions}
          textColor={textColor}
          textVariant="newH2"
          value={quantity}
        />
      )}
    </Block>
  );
};

export default QuantityActions;

const styles = StyleSheet.create({
  numberInput: {
    width: 100,
    height: 44,
    marginTop: 21,
  },
});
